// src/services/offlineSync.js — decides what may be queued, and hooks axios.
//
// Field pages call the API three different ways (the shared `api` instance, a raw
// `axios.post(...)` with a manual auth config, and `fetch` for downloads), so the
// interceptor is installed on BOTH the shared instance and the global axios
// default. Anything not on the whitelist below behaves exactly as it does today.
//
// WHAT IS SAFE TO QUEUE
//
// Only append-only, crew-scoped writes. A queued op can land an hour after the
// crew made it, so anything last-write-wins is excluded on purpose:
//
//   queueable      a note, a photo, a clock punch, a checklist tick — each one
//                  adds a row or moves a field only that crew owns. Replaying it
//                  late is still correct.
//   NOT queueable  PUT /jobs/:id (TimeTracking.js:542 flips job status this way),
//                  deletes, billing, email. Replaying these would silently
//                  overwrite whatever the office changed while the crew was out
//                  of signal. They fail loudly offline, which is the right answer.

import axios from 'axios';
import { enqueue, newOpId, placeholderFor, drain, storageIsTight } from './outbox';

// Each rule matches a path relative to /api. `label` builds the text the crew
// sees in the pending list.
const QUEUEABLE = [
  {
    test: (m, p) => m === 'post' && p === '/time-entries/clock-in',
    kind: 'clock-in',
    label: () => 'Clock in',
    // Publishes its server id so a later offline clock-out can find this entry.
    publishes: true,
  },
  {
    test: (m, p) => m === 'post' && p === '/time-entries/clock-in-crew',
    kind: 'clock-in-crew',
    label: () => 'Crew clock in',
    publishes: true,
  },
  {
    test: (m, p) => m === 'post' && /^\/time-entries\/[\w-]+\/clock-out$/.test(p),
    kind: 'clock-out',
    label: () => 'Clock out',
  },
  {
    test: (m, p) => m === 'post' && /^\/time-entries\/clock-out-job\/\d+$/.test(p),
    kind: 'clock-out-job',
    label: () => 'Crew clock out',
  },
  {
    test: (m, p) => m === 'post' && /^\/time-entries\/[\w-]+\/(start-break|end-break)$/.test(p),
    kind: 'break',
    label: (p) => (p.endsWith('start-break') ? 'Start break' : 'End break'),
  },
  {
    test: (m, p) => m === 'post' && /^\/time-entries\/[\w-]+\/(pause|resume)$/.test(p),
    kind: 'timer',
    label: (p) => (p.endsWith('pause') ? 'Pause timer' : 'Resume timer'),
  },
  {
    test: (m, p) => m === 'post' && /^\/time-entries\/(pause|resume)-crew\/\d+$/.test(p),
    kind: 'timer-crew',
    label: (p) => (p.includes('pause') ? 'Pause crew timer' : 'Resume crew timer'),
  },
  {
    test: (m, p) => m === 'post' && /^\/jobs\/\d+\/comments$/.test(p),
    kind: 'note',
    label: () => 'Job note',
  },
  {
    test: (m, p) => m === 'post' && /^\/jobs\/\d+\/photos$/.test(p),
    kind: 'photo',
    label: () => 'Photo',
  },
  {
    // The close-out modal has no cancel, so a crew in a dead zone would be stuck
    // if this couldn't queue. Unlike the append-only ops above this is an UPDATE,
    // which is normally kept online-only — it's safe here because the server
    // resolves conflicts by field time (conditions_recorded_at), so a stale replay
    // can't clobber a newer correction.
    test: (m, p) => m === 'post' && /^\/jobs\/\d+\/field-conditions$/.test(p),
    kind: 'conditions',
    label: () => 'Field conditions',
  },
  {
    test: (m, p) => m === 'put' && /^\/field-checklists\/\d+\/save$/.test(p),
    kind: 'checklist',
    label: () => 'Field checklist',
  },
  {
    test: (m, p) => m === 'post' && /^\/field-checklists\/\d+\/items\/\d+\/comments$/.test(p),
    kind: 'checklist-note',
    label: () => 'Checklist note',
  },
];

/** Strip baseURL/origin/query so rules can match a clean `/jobs/12/photos`. */
function normalizePath(config) {
  let url = config.url || '';
  // Raw axios calls pass an absolute URL built from API_URL; the shared instance
  // passes a path relative to baseURL.
  url = url.replace(/^https?:\/\/[^/]+/, '');
  // Raw calls carry the full /api/... path; shared-instance calls are already
  // relative to baseURL and need no trimming.
  const apiIdx = url.indexOf('/api/');
  if (apiIdx >= 0) url = url.slice(apiIdx + 4);
  return url.split('?')[0].replace(/\/$/, '') || '/';
}

function matchRule(config) {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'post' && method !== 'put') return null;
  const path = normalizePath(config);
  const rule = QUEUEABLE.find((r) => r.test(method, path));
  return rule ? { rule, path, method } : null;
}

function isNetworkFailure(error) {
  // No response at all = never reached the server. A 5xx DID reach it, so it is
  // not an offline case and must surface to the user as it does today.
  if (error.response) return false;
  return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !!error.request;
}

function extractJobId(path) {
  const m = path.match(/^\/jobs\/(\d+)\//);
  return m ? Number(m[1]) : null;
}

/**
 * Pull the pieces the outbox needs out of an axios config. FormData can't be
 * stored in IndexedDB directly, so photos are split into plain fields + the Blob.
 */
async function describePayload(config) {
  const data = config.data;

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const form = [];
    let blob = null;
    let blobName = null;
    for (const [k, v] of data.entries()) {
      if (v instanceof Blob) {
        blob = v;
        blobName = v.name || 'photo.jpg';
      } else {
        form.push([k, v]);
      }
    }
    return { form, blob, blobName, blobType: blob ? blob.type : null };
  }

  if (typeof data === 'string') {
    try {
      return { data: JSON.parse(data) };
    } catch (e) {
      return { data: {} };
    }
  }
  return { data: data || {} };
}

// A queued write resolves as if it succeeded so the crew's flow continues —
// the required end-of-job note closes, the timer stops, the photo card appears.
// `__queued` lets a caller tell the difference when it matters.
function queuedResponse(config, op) {
  return {
    data: { __queued: true, client_op_id: op.opId, id: op.publishesPlaceholder || null },
    status: 202,
    statusText: 'Queued offline',
    headers: {},
    config,
    __queued: true,
  };
}

async function handleOfflineWrite(error) {
  const config = error.config || {};
  if (config.__isDrain) return Promise.reject(error); // never re-queue the drain

  const matched = matchRule(config);
  if (!matched) return Promise.reject(error);

  const { rule, path } = matched;
  const payload = await describePayload(config);

  // Photos are the only multi-MB payload. Refuse before the browser starts
  // evicting the queue to make room, and tell the crew plainly.
  if (rule.kind === 'photo' && (await storageIsTight())) {
    return Promise.reject(
      Object.assign(new Error('No offline storage left for photos — sync when you have signal.'), {
        __offlineStorageFull: true,
      })
    );
  }

  const opId = newOpId();
  const op = await enqueue({
    opId,
    method: matched.method,
    url: path,
    kind: rule.kind,
    label: rule.label(path),
    jobId: extractJobId(path),
    occurredAt: new Date().toISOString(),
    publishesPlaceholder: rule.publishes ? placeholderFor(opId) : null,
    ...payload,
  });

  return Promise.resolve(queuedResponse(config, op));
}

/**
 * Rewrite a clock-out URL that points at a clock-in still sitting in the queue.
 * The placeholder is swapped for the real entry id during the drain.
 */
export function rewritePlaceholderUrl(url, placeholder) {
  return url.replace(/\/time-entries\/[\w-]+\//, `/time-entries/${placeholder}/`);
}

let installed = false;

/** Install on the shared instance and on global axios. Called once from api.js. */
export function installOfflineInterceptor(sharedInstance) {
  if (installed) return;
  installed = true;

  const onError = (error) => {
    if (!isNetworkFailure(error)) return Promise.reject(error);
    return handleOfflineWrite(error);
  };

  if (sharedInstance) {
    sharedInstance.interceptors.response.use((r) => r, onError);
  }
  // Field pages (TimeTracking, JobDetail, FieldChecklistModal) call axios
  // directly rather than through the shared instance.
  axios.interceptors.response.use((r) => r, onError);

  // A successful live write is the cheapest signal that we're back online.
  const onSuccess = (response) => {
    if (!response.__queued) drain();
    return response;
  };
  if (sharedInstance) sharedInstance.interceptors.response.use(onSuccess);
  axios.interceptors.response.use(onSuccess);
}

export { QUEUEABLE };
